import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ServicesSection from '../components/ServicesSection';
import AdventureSection from '../components/AdventureSection';
import '../styles/Home.css';

const Home = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const features = [
    {
      title: 'World-Class Destinations',
      description: 'Explore the finest locations across the globe',
      icon: '🌎',
    },
    {
      title: 'Personalized Planning',
      description: 'Customized travel experiences for your unique desires',
      icon: '📋',
    },
    {
      title: 'Local Expertise',
      description: 'Insider knowledge for authentic adventures',
      icon: '🎯',
    },
    {
      title: 'Visa Specialist',
      description: 'Expert assistance with visa applications and documentation',
      icon: '✈️',
    },
  ];

  const popularDestinations = [
    {
      title: 'Kerala Backwaters',
      description: 'God\'s own country experience',
      image: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    },
    {
      title: 'Rajasthan Heritage',
      description: 'Royal palaces and desert adventures',
      image: 'https://images.unsplash.com/photo-1599661046289-e31897846e41?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    },
    {
      title: 'Goa Beaches',
      description: 'Sun, sand and seafood paradise',
      image: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    },
    {
      title: 'Himachal Mountains',
      description: 'Serene mountain getaways',
      image: 'https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    },
    {
      title: 'Andaman Islands',
      description: 'Pristine beaches and coral reefs',
      image: 'https://images.unsplash.com/photo-1589979481223-deb893043163?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    },
    {
      title: 'Varanasi Spiritual Tour',
      description: 'Ancient spiritual experience',
      image: 'https://images.unsplash.com/photo-1561361058-c24cecae35ca?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    },
  ];

  return (
    <div className="hm-home">
      {/* Hero Section with Video Background */}
      <div className="hm-hero">
        {/* Video background with fallback to image */}
        <div className="hm-hero-background">
          <video
            className="hm-hero-video"
            src={process.env.PUBLIC_URL + "/Assets/Video/Heroes-adventure.mov"}
            autoPlay
            loop
            muted
            playsInline
            poster={process.env.PUBLIC_URL + "/Assets/image/background.jpg"}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          >
            <img
              src={process.env.PUBLIC_URL + "/Assets/image/background.jpg"}
              alt="Travel Adventure Background"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </video>
        </div>

        <div className="hm-hero-overlay">
          <motion.div className="hm-hero-content" {...fadeIn}>
            <motion.h1 className="hm-hero-title hm-gradient-text" {...fadeIn}>
              Discover Your Next Adventure
            </motion.h1>
            <motion.p className="hm-hero-subtitle" {...fadeIn}>
              Welcome to Excellent Travels, where dreams become destinations. Let us guide you
              through unforgettable journeys across the globe.
            </motion.p>
            <motion.div {...fadeIn}>
              <Link to="/services" className="hm-cta-button">
                Book Your Journey
                <span className="hm-arrow-icon">→</span>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="hm-background-image">
        <img src="/public/Assets/image/background.jpg" alt="Background" />
      </div>

      {/* Quote Request Section */}
      <section className="hm-quote-request">
        <motion.div
          className="hm-quote-container"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <h2>Get a Personalized Travel Quote</h2>
          <p>Contact our travel experts for customized pricing and packages tailored to your specific needs and preferences.</p>
          <Link to="/contact" className="hm-quote-button">
            Request a Free Quote
            <span className="hm-arrow-icon">→</span>
          </Link>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="hm-features">
        <motion.h2 className="hm-section-title hm-gradient-text" {...fadeIn}>
          Why Choose Excellent Travels?
        </motion.h2>
        <div className="hm-features-grid">
          {features.map((feature, index) => (
            <motion.div
              className="hm-feature-card"
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: "easeOut"
              }}
              whileHover={{
                translateY: -10,
                scale: 1.02
              }}
              viewport={{ once: true }}
            >
              <motion.div
                className="hm-feature-icon"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{
                  duration: 0.4,
                  delay: 0.1 + index * 0.1
                }}
                whileHover={{
                  scale: 1.1,
                  transition: { duration: 0.2 }
                }}
                viewport={{ once: true }}
              >
                {feature.icon}
              </motion.div>
              <h3 className="hm-feature-title">
                {feature.title}
              </h3>
              <p className="hm-feature-description">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      <ServicesSection />

      <section className="hm-destinations">
        <motion.h2
          className="hm-section-title hm-gradient-text"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          Popular Destinations
        </motion.h2>

        <div className="hm-destinations-grid">
          {popularDestinations.map((destination, index) => (
            <motion.div
              className="hm-destination-card"
              key={destination.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: 0.1 + index * 0.1,
                ease: "easeOut"
              }}
              whileHover={{
                translateY: -10,
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
              viewport={{ once: true }}
            >
              <div className="hm-destination-image-container">
                <img src={destination.image} alt={destination.title} className="hm-destination-image-img" />
              </div>

              <div className="hm-destination-content">
                <h3 className="hm-destination-title">
                  {destination.title}
                </h3>

                <p className="hm-destination-description">
                  {destination.description}
                </p>

                <div className="hm-destination-footer">
                  <Link to="/services/tour-packages" className="hm-learn-more-button">
                    Contact for Pricing
                    <span className="hm-arrow-icon">→</span>
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="hm-view-more-container"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Link to="/services/tour-packages" className="hm-view-more-button">
            View All Tour Packages
            <span className="hm-arrow-icon">→</span>
          </Link>
        </motion.div>
      </section>
      <AdventureSection />
    </div>
  );
};

export default Home;